import React, {
  ReactElement, ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  IconButton,
  Avatar,
  Box,
  CloseButton,
  Flex,
  HStack,
  VStack,
  useColorModeValue,
  Link,
  Drawer,
  DrawerContent,
  Text,
  useDisclosure,
  BoxProps,
  FlexProps,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList, MenuGroup, Image, Stack, Tooltip, useColorMode, useTheme, Portal, Input, InputGroup,
  InputLeftElement, Collapse, Badge, useToast,
} from '@chakra-ui/react';
import {
  FiBarChart2,
  FiBox,
  FiBookOpen,
  FiCheckSquare,
  FiGrid,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiLayers,
  FiSliders,
  FiSearch,
  FiRefreshCw,
  FiClipboard,
  FiEye, // item 1332 — customer ⋮ show (FiEyeOff / FiMoreVertical already imported)
  FiDollarSign,
  FiClock,
  FiDatabase,
  FiExternalLink,
  FiEyeOff,
  FiLogOut,
  FiMoreVertical,
  FiPlus,
  FiRepeat,
  FiUsers,
} from 'react-icons/fi';
import { FaThumbtack } from 'react-icons/fa';
import { Link as ReactLink, useHistory, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from 'react-query';
import {
  CloseIcon, EditIcon, StarIcon, RepeatIcon,
} from '@chakra-ui/icons';
import { NIL } from 'uuid';
import NavButton from './sidebar/NavButton'; // item 322f — extracted
import { useRnwyFleet, rnwyHealth } from '../hooks/useRnwyStatus'; // item 861
import { usePendingRecurringEnrollments } from '../hooks/useRecurringRevenues'; // item 1110
import SortableList from './sidebar/SortableList'; // item 633/699 — generic drag-reorder (projects + tools)
import AccountantNavBadge from './sidebar/AccountantNavBadge'; // item 482
import CustomerRequestsNavBadge from './sidebar/CustomerRequestsNavBadge'; // item 868a
import TasksNavBadge from './sidebar/TasksNavBadge'; // item 482
import TopBar from './sidebar/TopBar'; // item 322f cut 2
import { getLaunchSet, launchEnvironment } from './launchEnvironment'; // item 281
import {
  getImpersonatedRoleName, getImpersonatedUserId, IMPERSONATABLE_ROLES, startImpersonation, stopImpersonation,
} from '../utils/impersonation';
import ImpersonateUserGroup from './ImpersonateUserGroup'; // item 851 // item 323
import logo from '../assets/logo.png';
import IconProposals from '../assets/Icon-Proposals.svg';
import IconWorkOrder from '../assets/Icon-WorkOrder.svg';
import IconInvoice from '../assets/Icon-Invoice.svg';
import IconBills from '../assets/Icon-Bills.svg';
import IconPurchaseOrders from '../assets/Icon-PurchaseOrders.svg';
import IconCustomers from '../assets/Icon-Customers.svg';
import { getProjects } from '../hooks/useProjects';
import useMyFavoriteContacts from '../hooks/useMyFavoriteContacts';
import useContactFavorite from '../hooks/useContactFavorite';
import IconProjects from '../assets/Icon-Projects.svg';
import HeaderLogo from '../assets/BasixBooks-Header.png';
import { Project, ProjectStatus } from '../models/project';
import { PayType } from '../models/user';
import useProject from '../hooks/useProject';
import useEffectiveBrand from '../hooks/useEffectiveBrand';
import useCurrentUser from '../hooks/useCurrentUser';
import useContactAdministrative from '../hooks/useContactAdministrative'; // item 1332
import { visibleTools } from './toolsNav';
import BrandIcon from './BrandIcon';
import AuroraMenuButton from './AuroraMenuButton';
import { glowColorFor, isDarkColor } from '../theme/brandTheme';
import { useAppearance } from '../theme/AppearanceProvider';
import trimTransparentEdges from './logoTrim';
import HeroGlow from './dashboard/HeroGlow';
import { OPEN_SEARCH_EVENT } from './CommandPalette';
import BasixFooter from './BasixFooter';

// top bar height — content offsets and the sidebar inset derive from it
export const TOPBAR_HEIGHT = '72px';

// usernames are often the full email (e.g. chad+employee@pulseb2b.com), which overflows the
// sidebar/header label — show just the local part before the @ (Chad, 2026-06-28).
export const displayUserName = (name?: string): string => {
  if (!name) return '';
  return name.includes('@') ? name.split('@')[0] : name;
};

interface SidebarProps extends BoxProps {
  onClose: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}
const SidebarContent = ({
  onClose, collapsed, onToggleCollapse, ...rest
}: SidebarProps) => {
  const { isAuthenticated, user, logout } = useAuth0();
  const history = useHistory();
  // item 553 — default to PROJECTS (where Chad works) and REMEMBER the last mode per device,
  // so the sidebar never resets to "Basix Six" highlighted on its own (Chad).
  const [navMode, setNavModeState] = useState<string>(() => {
    try { return window.localStorage.getItem('basix:navMode') || 'projects'; } catch { return 'projects'; }
  });
  const setNavMode = (m: string) => {
    setNavModeState(m);
    try { window.localStorage.setItem('basix:navMode', m); } catch { /* best-effort */ }
  };
  const [projectSearch, setProjectSearch] = useState('');
  // item 1166 — reveal hidden (administrative) customers/projects. item 1332b (Chad: "it shows
  // two hidden but they are not hidden") — the reveal used to PERSIST per device, so one old click
  // kept everything revealed forever and "Hide from project sidebar" looked broken. Now it's a
  // transient peek: default hidden on every load, and hiding anything snaps it back off.
  const [showAdminProjects, setShowAdminProjects] = useState<boolean>(false);
  useEffect(() => {
    try { window.localStorage.removeItem('basix.sidebar.showAdmin'); } catch { /* no storage */ } // stale 1166 key
    const off = () => setShowAdminProjects(false);
    window.addEventListener('basix:sidebar-hidden', off); // a project ⋮ hid something (NavButton)
    return () => window.removeEventListener('basix:sidebar-hidden', off);
  }, []);
  const toggleShowAdminProjects = () => setShowAdminProjects((v) => !v);
  // item 121: which customer groups are expanded in the projects sidebar (session-only)
  const [expandedCustomers, setExpandedCustomers] = useState<Record<string, boolean>>({});
  // whether the "Closed projects" section (below the open ones) is expanded — collapsed by default
  const [showClosed, setShowClosed] = useState(false);
  // item 457 — whether the Invoices nav item's sub-tree (Recurring) is expanded
  const [invoicesExpanded, setInvoicesExpanded] = useState(false);
  // item 471 — whether the Proposals nav item's sub-tree (Hand-offs) is expanded
  const [proposalsExpanded, setProposalsExpanded] = useState(false);
  const { pathname: routePath } = useLocation();
  // item 34 — Team users see only Projects + Work Orders (+ Hand-Offs); hide the
  // financial document nav for them.
  const {
    isTeam, isSuperAdmin, isOutsideSales, isAdmin, isExecutive, canImpersonate, user: currentUser,
  } = useCurrentUser();
  const toast = useToast();
  // item 861 — live RNWY health per linked project (one shared fleet query app-wide):
  // a small traffic-lit dot on the sidebar project row via NavButton's badge slot.
  const { data: rnwyFleet } = useRnwyFleet();
  const rnwyByProject = useMemo(() => {
    // item 1124 — the fleet now carries one row per (project, system): aggregate per project
    // (open slots sum; ok only when every linked box answered) so the dot shows the
    // property's true combined headroom
    const map = new Map<string, {
      ok: boolean; venueCapacity: number; venuesRunning: number; venueTotalCapacity: number;
    }>();
    (rnwyFleet?.rows ?? []).forEach((r) => {
      const prev = map.get(r.projectId);
      map.set(r.projectId, {
        ok: (prev ? prev.ok : true) && r.ok,
        venueCapacity: (prev?.venueCapacity ?? 0) + (r.ok ? r.venueCapacity : 0),
        venuesRunning: (prev?.venuesRunning ?? 0) + (r.ok ? r.venuesRunning : 0),
        venueTotalCapacity: (prev?.venueTotalCapacity ?? 0) + (r.ok ? r.venueTotalCapacity : 0),
      });
    });
    return map;
  }, [rnwyFleet]);
  // item 1110 (Chad) — sold-but-not-enrolled subscriptions, resolved to the PROJECTS that own
  // them. The nag used to be a lone "N not enrolled" pill in the KPI strip: it counted the work
  // without ever pointing at it, so finding the project meant a trip to the Recurring page.
  // Marking the project's own row puts the nudge where the fix is (the in-project PENDING
  // callout, item 737, is one click away). Enrollment is admin-only, so only admins see it.
  const { data: pendingEnrollments } = usePendingRecurringEnrollments();
  const pendingEnrollProjects = useMemo(() => {
    if (!isAdmin) return new Set<string>();
    // item 884 — a DECLINED enrollment is settled; it never nags again.
    return new Set(
      (pendingEnrollments?.index ?? []).filter((r) => !r.declined).map((r) => r.projectId),
    );
  }, [pendingEnrollments, isAdmin]);
  // item 281 — open the user's configured page set, each in its own tab. Browsers block
  // multiple window.open from one gesture until the site is pop-up-whitelisted; warn if so.
  const handleLaunchEnvironment = () => {
    const set = getLaunchSet();
    if (set.length === 0) {
      toast({ status: 'info', title: 'Nothing to launch', description: 'Add pages in Settings → My Environment first.' });
      return;
    }
    const { blocked } = launchEnvironment(set);
    if (onClose) onClose();
    if (blocked > 0) {
      toast({
        status: 'warning',
        title: 'Allow pop-ups to launch everything',
        description: `${blocked} tab(s) were blocked. Allow pop-ups for this site (one time) and launch again.`,
        duration: 9000,
        isClosable: true,
      });
    }
  };
  const activeProjectId = routePath.match(/^\/project\/(?!edit)([^/]+)/)?.[1] ?? '';
  const { data: projects, status } = useQuery('projects', getProjects, {
    enabled: isAuthenticated && navMode === 'projects',
  });
  // item 237 — per-user customer pins: this user's favorited customers sort to the top
  const { data: favContactIds } = useMyFavoriteContacts();
  const contactFavorite = useContactFavorite();
  const contactAdministrative = useContactAdministrative(); // item 1332 — customer "Hide from project sidebar"
  const favCustomerSet = new Set(favContactIds ?? []);
  // item 699 (Chad) — the TOOLS pin feature was removed in favor of drag-reorder (SortableList).
  // item 242(d) — the TOOLS nav group collapses for a cleaner sidebar; state persists per-device
  const [toolsOpen, setToolsOpen] = useState(() => {
    try { return localStorage.getItem('basix:toolsOpen') !== 'false'; } catch { return true; }
  });
  const toggleTools = () => setToolsOpen((o) => {
    const next = !o;
    try { localStorage.setItem('basix:toolsOpen', String(next)); } catch { /* ignore */ }
    return next;
  });
  const { colorMode } = useColorMode();
  const theme = useTheme();
  // the sidebar is the brand's home now: logo stage on top, user at the foot
  const { brand } = useEffectiveBrand();
  const [trimmedLogo, setTrimmedLogo] = useState<string | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    if (brand?.logoDataUrl) {
      trimTransparentEdges(brand.logoDataUrl).then((t) => {
        if (alive) setTrimmedLogo(t);
      });
    } else {
      setTrimmedLogo(undefined);
    }
    return () => { alive = false; };
  }, [brand?.logoDataUrl]);
  const logoSrc = trimmedLogo ?? brand?.logoDataUrl;
  const sidebarLogoFilter = colorMode === 'light' ? 'brightness(0)' : 'brightness(0) invert(1)';
  const glow = glowColorFor(brand);
  const borderSubtle = colorMode === 'light' ? 'gray.100' : 'whiteAlpha.100';
  // item 958 — light-mode contrast: quiet sidebar glyphs (search magnifier, unpinned pin, hidden-projects
  // hover) hardcoded whiteAlpha/gray.300 and vanished on the light sidebar. Dark unchanged.
  const quietGlyph = colorMode === 'light' ? 'gray.400' : 'whiteAlpha.500';
  const pinIdleColor = colorMode === 'light' ? 'blackAlpha.400' : 'whiteAlpha.400';
  const revealHover = colorMode === 'light' ? 'gray.700' : 'gray.300';

  let logoStage = (
    <BrandIcon
      src={logo}
      color={colorMode === 'light' ? 'gray.800' : 'white'}
      w="60px"
      h="30px"
      mr={0}
    />
  );
  if (brand && logoSrc) {
    logoStage = (
      <Image
        src={logoSrc}
        maxH={collapsed ? '30px' : '64px'}
        maxW={collapsed ? '60px' : '200px'}
        objectFit="contain"
        filter={sidebarLogoFilter}
      />
    );
  } else if (brand?.companyName) {
    logoStage = (
      <Text fontWeight="bold" fontSize="lg" textAlign="center">
        {brand.companyName}
      </Text>
    );
  }
  const colorBack = colorMode === 'light' ? 'brandGreen.300' : 'brandGreen.700';
  const colorSelected = colorMode === 'light' ? 'brandGreen.400' : 'brandGreen.500';
  const colorHover = colorMode === 'light' ? 'brandGreen.200' : 'brandGreen.400';
  // tile text/icons contrast against the RESOLVED accent-derived tile color —
  // hardcoded white vanished on light accents (item 46, Chad's finding)
  const tileHex = (token: string) => {
    const [name, shadeKey] = token.split('.');
    return theme.colors?.[name]?.[shadeKey] as string;
  };
  const tileContent = (bgToken: string) => (isDarkColor(tileHex(bgToken)) ? 'white' : 'gray.900');
  // "Tesla" switch palette (item 46): the real accent hex + a deep shade for
  // the gradient thumb; content flips by the accent's luminance
  const { aurora, stealth, setStealth } = useAppearance();
  const accentHex = tileHex('brandGreen.500');
  const accentDeepHex = tileHex('brandGreen.700');
  const onAccent = isDarkColor(accentHex) ? 'white' : 'gray.900';
  // Aurora-glass segmented control palette (replaces the item-46 black "Tesla" track)
  const segTrackBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const segBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const segInactive = useColorModeValue('gray.600', 'whiteAlpha.700');
  const segActive = useColorModeValue('gray.900', 'white');
  // global-search trigger (item 107) — hoisted so the hook isn't called conditionally
  const searchTextColor = useColorModeValue('gray.700', 'whiteAlpha.800');
  const searchHoverBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.200');
  // Accountant skin: flat ledger-paper panel, no brand tint, no glow edges/tabs
  const panelSolidBg = 'ledgerSurface';
  const panelBorder = useColorModeValue('gray.200', 'whiteAlpha.300');
  // item 482 — the account menu must render SOLID: the sidebar's backdrop-filter makes an
  // in-panel MenuList see-through (item 97). Portal it out + paint a solid bg, like NavButton.
  const menuSolidBg = useColorModeValue('white', 'gray.800');
  const menuBorderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const panelGradient = useColorModeValue(
    `linear(to-b, ${accentHex}1f, rgba(255,255,255,0.78) 55%)`,
    `linear(to-b, ${accentHex}2e, rgba(255,255,255,0.05) 55%)`,
  );
  return (
    // floating inset panel (item 46 rework): margined off the screen edge and
    // the top bar, rounded + bordered like the content cards, never taller
    // than the viewport (the old h=full pushed the bottom off-screen)
    <Flex
      direction="column"
      transition={{ base: '3s ease', lg: 'none' }}
      bg={aurora ? undefined : panelSolidBg}
      bgGradient={aurora ? panelGradient : undefined}
      borderWidth={{ base: 0, lg: '1px' }}
      borderColor={aurora ? panelBorder : 'ledgerStroke'}
      borderRadius={{ base: 0, lg: aurora ? '2xl' : 'md' }}
      boxShadow={{ base: 'none', lg: aurora ? '0 8px 32px rgba(0,0,0,0.45)' : 'none' }}
      sx={aurora ? { backdropFilter: 'var(--bx-glass-panel)' } : undefined}
      w={{ base: 'full', lg: collapsed ? '88px' : '340px' }}
      pos="fixed"
      top={{ base: 0, lg: 4 }}
      left={{ base: 0, lg: 4 }}
      bottom={{ base: 0, lg: 4 }}
      h={{ base: 'full', lg: 'calc(100vh - 2rem)' }}
      {...rest}
    >
      <IconButton
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        icon={collapsed ? <FiChevronRight /> : <FiChevronLeft />}
        onClick={onToggleCollapse}
        size="xs"
        isRound
        position="absolute"
        top="24px"
        right="-12px"
        zIndex={1}
        display={{ base: 'none', lg: 'inline-flex' }}
        variant="outline"
        bg={useColorModeValue('white', 'gray.800')}
        boxShadow="md"
      />
      {aurora && (
        <Box
          position="absolute"
          top="24px"
          bottom="24px"
          right="-1px"
          w="2px"
          borderRadius="full"
          bgGradient={`linear(to-b, transparent, ${glow}, transparent)`}
          boxShadow={`0 0 12px ${glow}`}
          display={{ base: 'none', lg: 'block' }}
        />
      )}
      {/* pinned brand header: logo + mode switch never scroll away;
          no logo in the collapsed rail — it can't render at icon width (item 47) */}
      {!collapsed && (
        <Flex direction="column" align="center" px={3} pt={3} pb={5}>
          {/* item 327 easter egg (Chad) — double-tap the company logo to flip STEALTH
              mode: every number masks / unmasks. Quiet toggle for client demos. */}
          <Box onDoubleClick={() => setStealth(!stealth)} userSelect="none">
            {logoStage}
          </Box>
          {/* item 77 — company slogan under the brand */}
          {brand?.slogan && (
            <Text
              mt={2}
              fontSize="0.7rem"
              fontStyle="italic"
              textAlign="center"
              color={colorMode === 'light' ? 'gray.500' : 'whiteAlpha.600'}
            >
              {brand.slogan}
            </Text>
          )}
        </Flex>
      )}
      {collapsed && !isOutsideSales
      && (
        <VStack
          spacing={1}
          mx="auto"
          mt={4}
          mb={2}
          p="5px"
          borderRadius="2xl"
          bg={segTrackBg}
          borderWidth="1px"
          borderColor={segBorder}
          sx={{ backdropFilter: 'var(--bx-glass-chip)' }}
        >
          <Tooltip label="PROJECTS" placement="right" hasArrow>
            <Flex
              w="46px"
              h="42px"
              align="center"
              justify="center"
              borderRadius="xl"
              cursor="pointer"
              transition="all 0.25s"
              bg={!aurora && navMode === 'projects' ? accentHex : undefined}
              bgGradient={aurora && navMode === 'projects' ? `linear(135deg, ${accentHex}, ${accentDeepHex})` : undefined}
              boxShadow={aurora && navMode === 'projects' ? `0 0 16px -2px ${accentHex}` : undefined}
              color={navMode === 'projects' ? onAccent : segInactive}
              _hover={navMode === 'projects' ? undefined : { color: 'brandGreen.400' }}
              onClick={() => setNavMode('projects')}
            >
              <FiLayers size="20px" />
            </Flex>
          </Tooltip>
          <Tooltip label="BASIX SIX" placement="right" hasArrow>
            <Flex
              w="46px"
              h="42px"
              align="center"
              justify="center"
              borderRadius="xl"
              cursor="pointer"
              transition="all 0.25s"
              bg={!aurora && navMode === 'basix' ? accentHex : undefined}
              bgGradient={aurora && navMode === 'basix' ? `linear(135deg, ${accentHex}, ${accentDeepHex})` : undefined}
              boxShadow={aurora && navMode === 'basix' ? `0 0 16px -2px ${accentHex}` : undefined}
              onClick={() => setNavMode('basix')}
            >
              <BrandIcon src={logo} color={navMode === 'basix' ? onAccent : segInactive} w="36px" h="20px" mr={0} />
            </Flex>
          </Tooltip>
        </VStack>
      )}
      {!collapsed
      && (
      <Box px={3} pb={3}>
        <CloseButton
          display={{ base: 'flex', lg: 'none' }}
          ml="auto"
          mb={2}
          onClick={onClose}
        />
        {/* mode switch (Aurora rebuild): flat underline tabs — app-like, not a gadget.
            item 272 — hidden for Outside Sales (their portal has no Projects/Basix Six modes) */}
        {!isOutsideSales && (
        <Flex position="relative" mb={1}>
          {([
            { mode: 'projects', label: 'PROJECTS' },
            { mode: 'basix', label: 'BASIX SIX' },
          ] as const).map(({ mode, label }) => {
            const active = navMode === mode;
            return (
              <Box
                key={mode}
                flex={1}
                py={2}
                textAlign="center"
                position="relative"
                cursor="pointer"
                color={active ? segActive : segInactive}
                fontSize="xs"
                fontWeight="bold"
                letterSpacing="0.14em"
                transition="color 0.2s"
                _hover={active ? undefined : { color: 'brandGreen.400' }}
                onClick={() => setNavMode(mode)}
              >
                {label}
                {active && (
                  <Box
                    position="absolute"
                    bottom={0}
                    left="18%"
                    right="18%"
                    h="2px"
                    borderRadius="full"
                    bg={aurora ? undefined : accentHex}
                    bgGradient={aurora ? `linear(to-r, transparent, ${accentHex}, transparent)` : undefined}
                    boxShadow={aurora ? `0 0 10px ${accentHex}` : 'none'}
                  />
                )}
              </Box>
            );
          })}
          <Box position="absolute" bottom={0} left={0} right={0} h="1px" bg={segBorder} />
        </Flex>
        )}
      </Box>
      )}
      <Box flex="1" minH={0} overflowY="auto" pb={3}>
        {/* item 272 — Outside Sales portal: a minimal, self-service-only nav */}
        {isOutsideSales && (
          <>
            <NavButton
              icon={<BrandIcon src={IconCustomers} color="currentColor" boxSize="22px" />}
              name="My Book"
              onClick={onClose}
              link="/my-book"
              collapsed={collapsed}
            />
            <NavButton
              icon={<FiSliders />}
              name="appearance"
              onClick={onClose}
              link="/appearance"
              collapsed={collapsed}
            />
          </>
        )}
        {!isOutsideSales && navMode === 'basix'
        && (
          <>
            <Tooltip label="SEARCH (⌘K)" placement="right" hasArrow isDisabled={!collapsed}>
              <HStack
                mx={2}
                my={0.5}
                px={2}
                py={1.5}
                borderRadius="xl"
                cursor="pointer"
                role="group"
                justifyContent={collapsed ? 'center' : 'flex-start'}
                color={searchTextColor}
                _hover={{ bg: searchHoverBg }}
                onClick={() => { window.dispatchEvent(new Event(OPEN_SEARCH_EVENT)); if (onClose) onClose(); }}
              >
                <Box fontSize="18px" display="flex"><FiSearch /></Box>
                {!collapsed && <Text fontSize="sm">Search</Text>}
                {!collapsed && (
                  <Box ml="auto" fontSize="xs" opacity={0.6} fontWeight="semibold">⌘K</Box>
                )}
              </HStack>
            </Tooltip>
            <NavButton
              icon={<FiGrid />}
              name="dashboard"
              onClick={onClose}
              link="/"
              collapsed={collapsed}
            />
            {!isTeam && (
            <>
              {/* item 471 — Proposals is an expandable tree: Hand-offs (the numbers-free
                  deployment briefs) nest under it, exactly like Invoices ▸ Subscriptions. */}
              <HStack spacing={0} align="center" pr={collapsed ? 0 : 2}>
                <Box flex={1} minW={0}>
                  <NavButton
                    icon={<BrandIcon src={IconProposals} color="currentColor" boxSize="22px" />}
                    name="proposals"
                    onClick={onClose}
                    link="/proposals"
                    collapsed={collapsed}
                  />
                </Box>
                {!collapsed && (
                  <Box
                    as="button"
                    type="button"
                    p={1}
                    borderRadius="md"
                    color={searchTextColor}
                    _hover={{ bg: searchHoverBg }}
                    sx={{
                      '&:focus': { outline: 'none' },
                      '&:focus-visible': { boxShadow: 'outline' },
                    }}
                    aria-label={proposalsExpanded ? 'Collapse proposals' : 'Expand proposals'}
                    onClick={() => setProposalsExpanded((v) => !v)}
                  >
                    <Box
                      as={FiChevronDown}
                      fontSize="14px"
                      transform={proposalsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                      transition="transform 0.15s ease"
                    />
                  </Box>
                )}
              </HStack>
              {!collapsed && (
                <Collapse in={proposalsExpanded} animateOpacity>
                  <Box pl={5}>
                    <NavButton
                      icon={<FiClipboard />}
                      name="Hand-offs"
                      onClick={onClose}
                      link="/handoffs"
                      collapsed={collapsed}
                      dense
                    />
                  </Box>
                </Collapse>
              )}
            </>
            )}
            <NavButton
              icon={<BrandIcon src={IconWorkOrder} color="currentColor" boxSize="22px" />}
              name="work orders"
              onClick={onClose}
              link="/workOrders"
              collapsed={collapsed}
            />
            {/* item 471 — the Team role has no Proposals nav, but hand-offs ARE theirs; give
                them a direct Hand-offs entry alongside Work Orders. */}
            {isTeam && (
            <NavButton
              icon={<FiClipboard />}
              name="Hand-offs"
              onClick={onClose}
              link="/handoffs"
              collapsed={collapsed}
            />
            )}
            {!isTeam && !isExecutive && (
            <>
              {/* item 457 — Invoices is an expandable tree: its sub-views (Recurring/
                  subscriptions) nest under it, like customers group projects. */}
              {/* item 594 — Invoices/Bills/POs are financial: hidden from Executive. */}
              <HStack spacing={0} align="center" pr={collapsed ? 0 : 2}>
                <Box flex={1} minW={0}>
                  <NavButton
                    icon={<BrandIcon src={IconInvoice} color="currentColor" boxSize="22px" />}
                    name="invoice"
                    onClick={onClose}
                    link="/invoices"
                    collapsed={collapsed}
                  />
                </Box>
                {!collapsed && (
                  <Box
                    as="button"
                    type="button"
                    p={1}
                    borderRadius="md"
                    color={searchTextColor}
                    _hover={{ bg: searchHoverBg }}
                    sx={{
                      '&:focus': { outline: 'none' },
                      '&:focus-visible': { boxShadow: 'outline' },
                    }}
                    aria-label={invoicesExpanded ? 'Collapse invoices' : 'Expand invoices'}
                    onClick={() => setInvoicesExpanded((v) => !v)}
                  >
                    <Box
                      as={FiChevronDown}
                      fontSize="14px"
                      transform={invoicesExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
                      transition="transform 0.15s ease"
                    />
                  </Box>
                )}
              </HStack>
              {!collapsed && (
                <Collapse in={invoicesExpanded} animateOpacity>
                  <Box pl={5}>
                    <NavButton
                      icon={<FiRefreshCw />}
                      name="Subscriptions"
                      onClick={onClose}
                      link="/recurring"
                      collapsed={collapsed}
                      dense
                    />
                  </Box>
                </Collapse>
              )}
            </>
            )}
            {!isTeam && !isExecutive && (
            <NavButton
              icon={<BrandIcon src={IconBills} color="currentColor" boxSize="22px" />}
              name="bills"
              onClick={onClose}
              link="/bills"
              collapsed={collapsed}
            />
            )}
            {!isTeam && !isExecutive && (
            <NavButton
              icon={<BrandIcon src={IconPurchaseOrders} color="currentColor" boxSize="22px" />}
              name="purchase orders"
              onClick={onClose}
              link="/PurchaseOrder"
              collapsed={collapsed}
            />
            )}
            {/* item 696 (Chad) — CRM lives back in BASIX SIX (removed from TOOLS to avoid double-listing);
                it keeps its modal flow because /customers is a layer route. Team-visible (item 594). */}
            <NavButton
              icon={<FiUsers />}
              name="CRM"
              onClick={onClose}
              link="/customers"
              collapsed={collapsed}
            />
          </>
        )}
        {!isOutsideSales && navMode === 'projects'
        && (
          <>
            <NavButton
              icon={<FiPlus />}
              name="new project"
              link={`/project/edit/${NIL}`}
              collapsed={collapsed}
            />
            {/* search (item 92) — filter projects by name, client-side */}
            {!collapsed && (
              <Box px={3} py={2}>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none" color={quietGlyph}>
                    <FiSearch />
                  </InputLeftElement>
                  <Input
                    placeholder="Search projects…"
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    borderRadius="lg"
                    variant="filled"
                  />
                </InputGroup>
              </Box>
            )}
            {status === 'success' && projects
            && (() => {
              const q = projectSearch.trim().toLowerCase();
              // item 1166 (Chad: "lets not show it… a flag to show it or not") — hidden projects
              // leave the tree unless the reveal below is on; a search always looks through them.
              // item 1333 (Chad: "the break-even KPI has a leak") — hide on isHiddenFromSidebar,
              // NOT isAdministrative: hiding is cosmetic; isAdministrative (the project's own
              // flag) is what excludes money from the KPIs. The 1332 cascade fused the two and
              // every customer Chad hid to declutter silently left MRR/month-revenue/break-even.
              const isHidden = (p: Project) => (p.isHiddenFromSidebar ?? p.isAdministrative) === true;
              const adminCount = (projects.index ?? []).filter(isHidden).length;
              const list = (projects.index ?? []).filter((p) => (showAdminProjects || q || !isHidden(p)))
                .filter((p) => (!q
                  || p.name?.toLowerCase().includes(q)
                  || p.customerName?.toLowerCase().includes(q)));
              // closed projects live in their own collapsible section BELOW the open ones
              // (Chad 2026-06-22). A closed project leaves the open tree on close + returns on reopen.
              const openList = list.filter((p) => p.status !== ProjectStatus.Closed);
              const closedList = list.filter((p) => p.status === ProjectStatus.Closed);

              const renderProject = (p: Project) => {
                // item 861 — general RNWY health on the project row (linked projects only):
                // a small traffic-lit dot (green = room, amber ≤5, red ≤2, gray = no signal)
                const rnwyRow = rnwyByProject.get(p.id);
                let dotColor: string | undefined;
                if (rnwyRow) {
                  dotColor = rnwyRow.ok
                    ? { green: 'green.400', amber: 'orange.400', red: 'red.400' }[rnwyHealth(rnwyRow.venueCapacity)]
                    : 'gray.500';
                }
                return (
                  <NavButton
                    // item 596 — no icon on the expanded project rows (Chad); the collapsed
                    // icon-rail still needs a target, so keep the mark there only
                    icon={collapsed ? <BrandIcon src={IconProjects} color="currentColor" boxSize="22px" /> : undefined}
                    name={p.name}
                    link={`/project/${p.id}`}
                    key={p.id}
                    onClick={onClose}
                    menuObject={p}
                    collapsed={collapsed}
                    dense={!collapsed}
                    subscription={Boolean(p.hasSubscription)}
                    enrollmentPending={pendingEnrollProjects.has(p.id)}
                    signaturePending={p.pendingSignatureCount ?? 0} // item 1432
                    badge={rnwyRow && dotColor ? (
                      <Tooltip
                        label={rnwyRow.ok
                          ? `RNWY: ${rnwyRow.venuesRunning}/${rnwyRow.venueTotalCapacity} venues · ${rnwyRow.venueCapacity} open`
                          : 'RNWY: status unavailable'}
                        placement="right"
                      >
                        <Box w="7px" h="7px" borderRadius="full" bg={dotColor} flexShrink={0} />
                      </Tooltip>
                    ) : undefined}
                  />
                );
              };

              // collapsed (icon) rail: flat list of OPEN projects, favorites first
              // (closed are secondary — expand the sidebar to reach them)
              if (collapsed) {
                return [...openList]
                  .sort((a, b) => (Number(Boolean(b.isFavorite)) - Number(Boolean(a.isFavorite)))
                    // item 1283b (Chad) — alphabetical within each band, not last-entered
                    || (a.name ?? '').localeCompare(b.name ?? ''))
                  .map(renderProject);
              }

              // item 121/138: group projects under their customer — an expandable tree. The
              // group IS the project's customer (a project with no customer → "Unassigned",
              // sorted last). `prefix` namespaces the open vs closed expand state so a customer
              // that has both an open and a closed project doesn't share one toggle.
              const renderCustomerGroups = (groupList: Project[], prefix: string) => {
                const groups = new Map<string, Project[]>();
                groupList.forEach((p) => {
                  const key = p.customerName || 'Unassigned';
                  if (!groups.has(key)) groups.set(key, []);
                  groups.get(key)!.push(p);
                });
                // item 237 — a group's customer id (from its first project) + whether THIS
                // user pinned that customer; pinned customers sort to the top.
                const custIdOf = (k: string) => groups.get(k)?.[0]?.customerId;
                // item 1203 — the open-group tint + the child rail color (skin-aware)
                const groupTint = colorMode === 'light' ? 'blackAlpha.50' : 'whiteAlpha.50';
                const isFavCust = (k: string) => {
                  const cid = custIdOf(k);
                  return !!cid && favCustomerSet.has(cid);
                };
                return Array.from(groups.keys()).sort((a, b) => {
                  if (a === 'Unassigned') return 1;
                  if (b === 'Unassigned') return -1;
                  const fa = isFavCust(a);
                  const fb = isFavCust(b);
                  if (fa !== fb) return fa ? -1 : 1; // my pinned customers first
                  return a.localeCompare(b);
                }).map((cust) => {
                  const projs = groups.get(cust)!
                    .sort((a, b) => (Number(Boolean(b.isFavorite)) - Number(Boolean(a.isFavorite)))
                    // item 1283b (Chad) — alphabetical within each band, not last-entered
                    || (a.name ?? '').localeCompare(b.name ?? ''));
                  const containsActive = projs.some((p) => p.id === activeProjectId);
                  const expandKey = `${prefix}${cust}`;
                  // item 497 — an explicit `false` (the user clicked to collapse this group, or
                  // hit "Collapse all") must WIN over containsActive; otherwise the active
                  // project's group can never actually close, which read as "Collapse all
                  // doesn't work" (Chad). Undefined (never touched) still defers to auto-open.
                  const explicit = expandedCustomers[expandKey];
                  const isOpen = !!q || explicit === true || (containsActive && explicit !== false);
                  return (
                    // item 1203 (Chad: "delineate better") — an OPEN customer group gets a
                    // subtle tinted block so the family reads as one card; collapsed
                    // groups stay flat. mb gives customers more air than their children.
                    <Box
                      key={expandKey}
                      mb={1}
                      borderRadius="xl"
                      bg={isOpen ? groupTint : undefined}
                      transition="background 0.15s ease"
                      pb={isOpen ? 1 : 0}
                    >
                      {/* item 125: px instead of mx+w=full avoids the 16px overflow that
                          clipped the count badge at the right edge of the sidebar */}
                      <HStack
                        as="button"
                        type="button"
                        role="group"
                        w="full"
                        px={3}
                        py={1.5}
                        borderRadius="xl"
                        spacing={2}
                        color={searchTextColor}
                        _hover={{ bg: searchHoverBg }}
                        // item 322f — the raw <button> wore the browser's stock blue focus
                        // oval (and it stuck around after a click — Chad's screenshot).
                        // Show a ring for KEYBOARD focus only, in the theme's brand glow.
                        sx={{
                          '&:focus': { outline: 'none' },
                          '&:focus-visible': { boxShadow: 'outline' },
                        }}
                        onClick={() => setExpandedCustomers((s) => ({ ...s, [expandKey]: !isOpen }))}
                      >
                        <Box
                          as={FiChevronDown}
                          fontSize="14px"
                          flexShrink={0}
                          transform={isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}
                          transition="transform 0.15s ease"
                        />
                        {/* item 322f — isTruncated, NOT noOfLines={1}: line-clamp breaks at
                            word boundaries, so "NATIONAL TECHNOLOGIES" clamped to
                            "NATIONAL…" with dead space left over. Ellipsize at the real
                            available width instead; full name on hover. */}
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          isTruncated
                          minW={0}
                          flex={1}
                          textAlign="left"
                          title={cust}
                        >
                          {cust}
                        </Text>
                        {/* item 1110 — a COLLAPSED group would hide its projects' not-enrolled
                            marks entirely, so the group carries the mark until you open it.
                            item 1176 (Chad) — the mark sits OUTSIDE (left of) the pin: pin +
                            count are always present so they hold their columns; the sometimes-
                            there ⟳ floats leftmost where its absence can't shift anything. */}
                        {!isOpen && projs.some((p) => pendingEnrollProjects.has(p.id)) && (
                          <Tooltip label="A subscription here is sold but not enrolled — open to find it" placement="right" hasArrow>
                            <Box as={FiRepeat} flexShrink={0} fontSize="12px" color="orange.300" aria-label="Subscription not enrolled" />
                          </Tooltip>
                        )}
                        {/* item 237 — per-user pin: click the star to pin this customer to
                            the top of the tree (for you only). Hidden for Unassigned. */}
                        {cust !== 'Unassigned' && custIdOf(cust) && (
                          <Box
                            as={FaThumbtack}
                            flexShrink={0}
                            fontSize="12px"
                            // item 1203 — an UNPINNED pin only appears on row hover (it was a
                            // constant gray column fighting the tree indent); a pinned one
                            // stays lit — it's a state marker.
                            opacity={isFavCust(cust) ? 1 : 0}
                            transition="opacity 0.15s ease"
                            _groupHover={{ opacity: 1 }}
                            color={isFavCust(cust) ? 'yellow.400' : pinIdleColor}
                            _hover={{ color: isFavCust(cust) ? 'yellow.300' : 'yellow.200' }}
                            aria-label={isFavCust(cust) ? 'Unpin customer' : 'Pin customer'}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              const cid = custIdOf(cust);
                              if (cid) contactFavorite.mutate({ id: cid, isFavorite: !isFavCust(cust) });
                            }}
                          />
                        )}
                        {/* item 1332 (Chad: "what about 3 dots on the customer") — admin-only ⋮ on the
                            customer row: HIDE FROM / SHOW IN PROJECT SIDEBAR for the whole customer
                            (every project under it follows). Hover-revealed like the pin; the
                            MenuButton renders as a Box so it isn't a <button> inside this <button>. */}
                        {isAdmin && cust !== 'Unassigned' && custIdOf(cust) && (
                          <Menu placement="right-start" isLazy>
                            <MenuButton
                              as={Box}
                              flexShrink={0}
                              fontSize="14px"
                              lineHeight={0}
                              opacity={0}
                              transition="opacity 0.15s ease"
                              _groupHover={{ opacity: 1 }}
                              _hover={{ color: 'brand.100' }}
                              aria-label="Customer actions"
                              onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                              <Box as={FiMoreVertical} />
                            </MenuButton>
                            <Portal>
                              <MenuList
                                fontSize="sm"
                                bg={colorMode === 'light' ? 'white' : 'gray.800'}
                                boxShadow="xl"
                                zIndex={1600}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              >
                                {(() => {
                                  const hidden = !!projs[0]?.customerIsAdministrative;
                                  return (
                                    <MenuItem
                                      icon={hidden ? <FiEye /> : <FiEyeOff />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const cid = custIdOf(cust);
                                        if (cid) contactAdministrative.mutate({ id: cid, value: !hidden });
                                        if (!hidden) setShowAdminProjects(false); // item 1332b — hide means gone, now
                                      }}
                                    >
                                      {hidden ? 'SHOW IN PROJECT SIDEBAR' : 'HIDE FROM PROJECT SIDEBAR'}
                                    </MenuItem>
                                  );
                                })()}
                              </MenuList>
                            </Portal>
                          </Menu>
                        )}
                        {/* item 137: a proper count pill, not bare text.
                            item 1171 (Chad: "the pins don't align") — FIXED width: a 1-digit
                            and a 2-digit count used to differ by a few px, shoving every
                            group's pin to a different x. One width = one pin column. */}
                        <Badge
                          flexShrink={0}
                          borderRadius="full"
                          w="30px"
                          textAlign="center"
                          fontSize="0.65rem"
                          colorScheme="gray"
                          variant="subtle"
                        >
                          {projs.length}
                        </Badge>
                      </HStack>
                      <Collapse in={isOpen} animateOpacity>
                        {/* item 125: independently scrollable so a customer with many
                            projects doesn't push the sidebar tools off the bottom */}
                        {/* item 633 — drag-reorder within the group (favorites pinned top); the
                            grip arms native drag so a plain click always navigates. */}
                        {/* item 1203 — indent RAIL: a thin guide line under the chevron spans
                            the children, so where the rail ends, the customer's family ends */}
                        <Box
                          ml="19px"
                          pl={1}
                          borderLeftWidth="1px"
                          borderLeftColor={colorMode === 'light' ? 'blackAlpha.200' : 'whiteAlpha.200'}
                          maxH="220px"
                          overflowY="auto"
                        >
                          <SortableList
                            storageKey={`basix:projorder:${expandKey}`}
                            items={projs}
                            getId={(p) => p.id}
                            isPinned={(p) => Boolean(p.isFavorite)}
                            renderItem={renderProject}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  );
                });
              };

              const closedOpen = showClosed || !!q;
              // item 497 (Chad) — one expand-all/collapse-all for every customer group (open +
              // closed) + the closed-projects section, styled EXACTLY like the "TOOLS" section
              // header below (same chevron size/color/rotation + small-caps label) so every
              // collapsible affordance in the sidebar reads the same way.
              const allExpandKeys = Array.from(new Set([
                ...openList.map((p) => p.customerName || 'Unassigned'),
                ...closedList.map((p) => `closed:${p.customerName || 'Unassigned'}`),
              ]));
              // a group counts as "open" if explicitly true, OR it holds the active project and
              // hasn't been explicitly closed — mirrors the per-row isOpen formula above so this
              // header's state always agrees with what's actually on screen.
              const activeProject = [...openList, ...closedList].find((p) => p.id === activeProjectId);
              const activeKey = activeProject
                ? (openList.includes(activeProject) ? '' : 'closed:') + (activeProject.customerName || 'Unassigned')
                : undefined;
              const isKeyOpen = (k: string) => expandedCustomers[k] === true
                || (k === activeKey && expandedCustomers[k] !== false);
              const allCollapsed = showClosed === false && allExpandKeys.every((k) => !isKeyOpen(k));
              const toggleAllProjects = () => {
                setExpandedCustomers((s) => {
                  const next = { ...s };
                  allExpandKeys.forEach((k) => { next[k] = allCollapsed; });
                  return next;
                });
                setShowClosed(allCollapsed);
              };
              return (
                <>
                  {allExpandKeys.length > 0 && !q && (
                    <HStack
                      as="button"
                      type="button"
                      w="full"
                      px={3}
                      py={1}
                      mb={1}
                      spacing={2}
                      borderRadius="xl"
                      sx={{ '&:focus': { outline: 'none' }, '&:focus-visible': { boxShadow: 'outline' } }}
                      onClick={toggleAllProjects}
                      _hover={{ opacity: 0.8 }}
                    >
                      <Box
                        as={FiChevronDown}
                        fontSize="11px"
                        color={colorMode === 'light' ? 'gray.400' : 'whiteAlpha.400'}
                        transform={allCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'}
                        transition="transform 0.15s ease"
                      />
                      <Text
                        fontSize="0.65rem"
                        fontWeight="bold"
                        letterSpacing="0.18em"
                        color={colorMode === 'light' ? 'gray.400' : 'whiteAlpha.400'}
                      >
                        {allCollapsed ? 'EXPAND ALL' : 'COLLAPSE ALL'}
                      </Text>
                    </HStack>
                  )}
                  {renderCustomerGroups(openList, '')}
                  {closedList.length > 0 && (
                    <Box mt={3} pt={2} borderTopWidth="1px" borderTopColor={borderSubtle}>
                      <HStack
                        as="button"
                        type="button"
                        w="full"
                        px={3}
                        py={1.5}
                        borderRadius="xl"
                        spacing={2}
                        color={searchTextColor}
                        _hover={{ bg: searchHoverBg }}
                        // item 1513 — the one sidebar toggle still wearing the browser's stock
                        // focus oval (it stuck after a tap on the phone): same keyboard-only
                        // brand ring as every other row (see item 322f above)
                        sx={{ '&:focus': { outline: 'none' }, '&:focus-visible': { boxShadow: 'outline' } }}
                        onClick={() => setShowClosed((s) => !s)}
                      >
                        <Box
                          as={FiChevronDown}
                          fontSize="14px"
                          flexShrink={0}
                          transform={closedOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}
                          transition="transform 0.15s ease"
                        />
                        <Text fontSize="0.7rem" fontWeight="bold" letterSpacing="0.12em" flex={1} textAlign="left">
                          CLOSED PROJECTS
                        </Text>
                        <Badge
                          flexShrink={0}
                          borderRadius="full"
                          px={2}
                          minW="20px"
                          textAlign="center"
                          fontSize="0.65rem"
                          colorScheme="gray"
                          variant="subtle"
                        >
                          {closedList.length}
                        </Badge>
                      </HStack>
                      <Collapse in={closedOpen} animateOpacity>
                        {renderCustomerGroups(closedList, 'closed:')}
                      </Collapse>
                    </Box>
                  )}
                  {/* item 1166/1332 — the quiet reveal for hidden (administrative) customers/projects */}
                  {adminCount > 0 && (
                    <Text
                      px={4}
                      py={1.5}
                      fontSize="0.65rem"
                      color="gray.500"
                      cursor="pointer"
                      _hover={{ color: revealHover }}
                      onClick={toggleShowAdminProjects}
                    >
                      {/* item 1332/1332b (Chad) — plain words, and say which way it's going */}
                      {showAdminProjects
                        ? `Hide all (${adminCount} shown)`
                        : `Show all (${adminCount} hidden)`}
                    </Text>
                  )}
                </>
              );
            })()}
          </>
        )}
        {/* item 482 — the notification feed was merged into the header bell (one center,
            always visible, survives a collapsed rail) — see NotificationBell. */}
        {!isOutsideSales && (
        <Box mt={6} pt={3} borderTopWidth="1px" borderTopColor={borderSubtle}>
          {!collapsed
          && (
            <HStack
              as="button"
              type="button"
              w="full"
              px={5}
              pb={1}
              sx={{ '&:focus': { outline: 'none' }, '&:focus-visible': { boxShadow: 'outline' } }}
              spacing={1}
              onClick={toggleTools}
              _hover={{ opacity: 0.8 }}
            >
              <Box
                as={FiChevronDown}
                fontSize="11px"
                color={colorMode === 'light' ? 'gray.400' : 'whiteAlpha.400'}
                transform={toolsOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}
                transition="transform 0.15s ease"
              />
              <Text
                fontSize="0.65rem"
                fontWeight="bold"
                letterSpacing="0.18em"
                color={colorMode === 'light' ? 'gray.400' : 'whiteAlpha.400'}
              >
                TOOLS
              </Text>
            </HStack>
          )}
          <Collapse in={collapsed || toolsOpen} animateOpacity>
            {/* item 352 (Chad) — TOOLS are PINNABLE: hover a tool, click the pin, and it
                floats to the top (per-device, like customer pins). Data-driven list. */}
            {(() => {
              // item 475 — the TOOLS list + visibility rules now live in ./toolsNav, shared with
              // the tools-modal (LayerPanel) header switcher so the two never drift.
              const visible = visibleTools({
                isTeam,
                isExecutive, // item 594 — hides the financial tools (reports, accountant)
                // hourly AND a rate actually plugged in — that's who gets a timesheet
                isHourly: currentUser?.payType === PayType.Hourly && (currentUser?.hourlyRate ?? 0) > 0,
                isIndependentContractor: currentUser?.isIndependentContractor === true
                  || currentUser?.payType === PayType.IndependentContractor, // item 1079/1083
                isAdmin, // item 1080 — admins review ALL timesheets
              });
              // item 699/700 (Chad) — TOOLS are drag-reorderable (pin removed). My Environment is folded
              // into the SAME list (as an ACTION item, key 'my-environment') so it's draggable too and
              // participates in the persisted order. Same grab-and-move + up/down grip as projects.
              const toolItems = [
                ...visible,
                {
                  key: 'my-environment', icon: <FiExternalLink />, name: 'My Environment', link: '', teamOk: true,
                } as (typeof visible)[number],
              ];
              return (
                <SortableList
                  storageKey="basix.toolsOrder"
                  items={toolItems}
                  getId={(t) => t.key}
                  renderItem={(t) => (t.key === 'my-environment' ? (
                    <NavButton
                      icon={<FiExternalLink />}
                      name="My Environment"
                      onClick={() => { handleLaunchEnvironment(); onClose(); }}
                      collapsed={collapsed}
                    />
                  ) : (
                    <NavButton
                      icon={t.icon}
                      name={t.name}
                      onClick={onClose}
                      link={t.link}
                      collapsed={collapsed}
                      // item 482 — iPhone-style status badge on the queue-bearing tools
                      // (item 868a added the customer-requests queue badge)
                      badge={(() => {
                        if (t.key === 'accountant') return <AccountantNavBadge collapsed={collapsed} />;
                        if (t.key === 'department-tasks') return <TasksNavBadge collapsed={collapsed} />;
                        if (t.key === 'customer-requests') return <CustomerRequestsNavBadge collapsed={collapsed} />;
                        return undefined;
                      })()}
                    />
                  ))}
                />
              );
            })()}
            {/* item 500 — Appearance stays in the user (avatar) menu below: a personal, per-device
                display option, not a company tool. */}
            {/* item 203/368 — the Dev Data tool is HIDDEN since 2026-07-02: real data
                lives in this DB now. The backend endpoints also 404 unless
                BASIX_DEV_DATA=true is deliberately set. Restore the NavButton here
                (link=/dev-data) + the App route only alongside that flag. */}
          </Collapse>
        </Box>
        )}
      </Box>
      <Box px={3} py={3} borderTopWidth="1px" borderTopColor={borderSubtle}>
        <Flex
          align="center"
          direction={collapsed ? 'column' : 'row'}
          gridGap={2}
        >
          <Menu placement="right-end">
            <MenuButton sx={{ '&:focus': { boxShadow: 'none' }, '&:focus-visible': { boxShadow: 'outline' } }}>
              <HStack spacing={2}>
                {/* item 929 — Basix profile photo first; Auth0 picture is only a fallback */}
                <Avatar size="sm" src={(currentUser as any)?.photoUrl || user?.picture} />
                {!collapsed
                && (
                  <VStack alignItems="flex-start" spacing="1px" maxW="150px">
                    <Text fontSize="sm" noOfLines={1}>{displayUserName(user?.name)}</Text>
                    <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'whiteAlpha.600'}>
                      {/* item 594 — reflect the real role instead of always "Admin" */}
                      {/* eslint-disable-next-line no-nested-ternary */}
                      {isOutsideSales ? 'Outside Sales' : isExecutive ? 'Executive' : isTeam ? 'Team' : 'Admin'}
                    </Text>
                  </VStack>
                )}
              </HStack>
            </MenuButton>
            {/* item 501 — Portal the menu out of the sidebar's backdrop-filter stacking context;
                without it the glass sidebar traps the menu BELOW the layer-2 panel (Sales Bible
                bled over it in Chad's screenshot) no matter its zIndex. Same fix as the NavButton
                project menus (item 97). */}
            <Portal>
              <MenuList bg={aurora ? menuSolidBg : panelSolidBg} borderColor={aurora ? menuBorderColor : 'ledgerStroke'} boxShadow="xl" zIndex={1600}>
                {/* item 320 — personal settings (incl. profile photo) live on the user's own
                  Edit User Info page */}
                {currentUser?.id && (
                <MenuItem onClick={() => history.push(`/user/${currentUser.id}`)}>My profile</MenuItem>
                )}
                {/* item 500 — personal, per-device options live here (not in TOOLS) */}
                <MenuItem icon={<FiSliders />} onClick={() => history.push('/appearance')}>Appearance</MenuItem>
                {/* item 696 — My Environment moved to the TOOLS list */}
                {/* item 272 — Outside Sales is external: no company Settings / Users mgmt */}
                {!isOutsideSales && <MenuItem onClick={() => history.push('/settings/')}>Settings</MenuItem>}
                {!isTeam && !isOutsideSales && <MenuItem onClick={() => history.push('/users/')}>Users</MenuItem>}
                {/* item 327 — one tap hides every number app-wide (client demos) */}
                <MenuItem icon={<FiEyeOff />} onClick={() => setStealth(!stealth)}>
                  {stealth ? 'Stealth mode: ON — show numbers' : 'Stealth mode — hide numbers'}
                </MenuItem>
                {/* item 323 — testing tool: view + act as another role (410: admins too) */}
                {canImpersonate && !getImpersonatedRoleName() && (
                <>
                  <MenuDivider />
                  <MenuGroup title="Impersonate role (testing)">
                    {IMPERSONATABLE_ROLES.map((r) => (
                      <MenuItem key={r.value} onClick={() => startImpersonation(r.value)}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </MenuGroup>
                  <ImpersonateUserGroup />
                </>
                )}
                {(getImpersonatedRoleName() || getImpersonatedUserId()) && (
                <MenuItem color="orange.400" onClick={stopImpersonation}>Exit impersonation</MenuItem>
                )}
                <MenuDivider />
                <MenuItem icon={<FiLogOut />} onClick={() => logout({ returnTo: window.location.origin })}>Sign out</MenuItem>
              </MenuList>
            </Portal>
          </Menu>
          {/* item 983 (Chad) — an always-VISIBLE sign out, not just the menu item:
              nobody could find "Sign out" buried behind the avatar menu */}
          <Tooltip label="Sign out" placement="right">
            <IconButton
              aria-label="Sign out"
              icon={<FiLogOut />}
              size="sm"
              variant="ghost"
              ml={collapsed ? 0 : 'auto'}
              onClick={() => logout({ returnTo: window.location.origin })}
            />
          </Tooltip>
        </Flex>
      </Box>
    </Flex>
  );
};

SidebarContent.defaultProps = {
  collapsed: false,
  onToggleCollapse: undefined,
};

export default function SidebarWithHeader(
  { children, header }: { children: ReactNode; header?: ReactNode },
) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  // item 326b (Jake) — the LIVE dashboard wants EVERY pixel: no sidebar, no top bar,
  // just the routed content (the page brings its own exit/undock controls)
  const { pathname: chromePath } = useLocation();
  const chromeless = chromePath === '/dashboard/live' || chromePath === '/chat'; // item 395
  // collapsed = icon-only rail (item 46); remembered across sessions
  // item 1063 (Chad: tablet pass) — on a first visit from a TABLET-width screen
  // (< 1280px) the full 372px sidebar eats a third of the viewport: default the rail
  // collapsed there. An explicit user toggle (saved value) always wins.
  const [collapsed, setCollapsed] = useState(() => {
    const saved = window.localStorage.getItem('basix-sidebar-collapsed');
    if (saved !== null) return saved === '1';
    return window.innerWidth < 1280;
  });
  const toggleCollapsed = () => setCollapsed((c) => {
    window.localStorage.setItem('basix-sidebar-collapsed', c ? '0' : '1');
    return !c;
  });

  // item 1056b (Chad: "once it's stuck, one more scroll and the entire page moves") —
  // SCROLL CHAINING kill switch. When the content region hits its end, the browser hands
  // the leftover wheel delta to the DOCUMENT; anything rendered past the viewport (a
  // portal, a glow, an overlay) gives the document scroll range, and the whole app —
  // sticky header included — slides. While the chrome shell is mounted: hard-lock
  // html/body overflow, kill overscroll chaining, and snap any window scroll back to 0.
  // Chromeless/public routes (live dash, chat, /public/*) never mount this branch and
  // keep their normal body scroll.
  useEffect(() => {
    if (chromeless) return undefined;
    const html = document.documentElement;
    const { body } = document;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlOverscroll: html.style.getPropertyValue('overscroll-behavior'),
      bodyOverscroll: body.style.getPropertyValue('overscroll-behavior'),
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.setProperty('overscroll-behavior', 'none');
    body.style.setProperty('overscroll-behavior', 'none');
    const heal = () => {
      if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
    };
    heal();
    window.addEventListener('scroll', heal, { passive: true });
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      html.style.setProperty('overscroll-behavior', prev.htmlOverscroll);
      body.style.setProperty('overscroll-behavior', prev.bodyOverscroll);
      window.removeEventListener('scroll', heal);
    };
  }, [chromeless]);

  // (after every hook, so the hook order never changes across navigations)
  if (chromeless) {
    return (
      <Box minH="100vh">
        {children}
        <BasixFooter />
      </Box>
    );
  }

  return (
    // the canvas itself is the theme's ambient-glow body background — the
    // shell stays transparent so the glow shows through (Sizzle skin).
    // item 369a — hard-locked to the viewport: the BODY never scrolls; the only
    // scrollbar in the app is the content region's.
    // item 1056 — SELF-HEALING lock: scrollIntoView (or any programmatic scroll) can still
    // shift an overflow:hidden box; if anything ever nudges this container, snap it back
    // so the sticky header slot can never silently slide out of view again.
    // item 1505 (Chad, iPhone screenshots) — 100vh on iOS Safari is the LARGEST viewport (toolbars
    // retracted), so the shell's bottom edge sat under the browser chrome while the bars were out;
    // 100dvh tracks the live viewport where supported (desktop: identical). viewport-fit=cover
    // (index.html) lets the page run under the notch in landscape, so the shell pads the side
    // insets back out (0 on every desktop / portrait phone).
    <Box
      sx={{
        height: '100vh',
        '@supports (height: 100dvh)': { height: '100dvh' },
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
      overflow="hidden"
      onScroll={(e) => { if (e.currentTarget.scrollTop !== 0) e.currentTarget.scrollTop = 0; }}
    >
      <SidebarContent
        onClose={() => onClose}
        display={{ base: 'none', lg: 'flex' }}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
      />
      <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement="left"
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size="full"
      >
        <DrawerContent>
          <SidebarContent onClose={onClose} />
        </DrawerContent>
      </Drawer>
      <TopBar onOpen={onOpen} />
      {/* item 361 — the shell owns ONE scroll region BELOW the header slot. The
          persistent dashboard lives in the slot (never scrolls, can't be overlapped);
          routed content scrolls inside; page titles stick to the scrollport with a
          constant offset. This replaces the measured --basix-appheader-h var whose
          drift kept re-breaking the pinned headers (items 313/353). */}
      <Box
        ml={{ base: 0, lg: collapsed ? '120px' : '372px' }}
        // item 1505 — same 100dvh fallback as the root lock (see above)
        sx={{ height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' } }}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        // item 1056 — same self-heal as the root lock (see above)
        onScroll={(e) => { if (e.currentTarget.scrollTop !== 0) e.currentTarget.scrollTop = 0; }}
      >
        {header && (
          <Box flexShrink={0} w="full" maxW="1440px" mx="auto" px={{ base: 4, md: 10 }}>
            {header}
          </Box>
        )}
        {/* item 539 — id'd so a newly-opened form can scroll this (the app's ONE scroll
            region) back to the top instead of stranding you where you were (Chad). */}
        {/* item 1056b — overscrollBehavior contain: reaching this region's end must never
            hand the leftover scroll to an ancestor/document (the page-slide bug) */}
        {/* item 1505 (Chad, iPhone screenshots: the page panned sideways) — overflowY alone
            computes overflow-x:auto, so one over-wide row anywhere on a page widened THIS box's
            scroll range. overflowX hidden pins the canvas; ledgers keep their own overflowX="auto"
            scrollers (nested scroll boxes are independent). */}
        <Box id="basix-content-scroll" flex="1" minH={0} overflowY="auto" overflowX="hidden" sx={{ overscrollBehavior: 'contain' }}>
          {/* contained, roomy content: centered max-width with real gutters. position
              relative so the hero glow can anchor to the top of the content column and
              the routed content layers above it (item 96 "super amazing top"). */}
          {/* item 1505 — phone bottom pad clears the iOS home indicator (env() is 0 elsewhere) */}
          <Box
            maxW="1440px"
            mx="auto"
            position="relative"
            px={{ base: 4, md: 10 }}
            pt={{ base: 3, md: 4 }}
            pb={{ base: 'calc(1rem + env(safe-area-inset-bottom))', md: 8 }}
          >
            <HeroGlow />
            <Box position="relative" zIndex={1}>
              {children}
            </Box>
            {/* item 369a — the footer lives INSIDE the scroll region: parked below the
                shell it added body-level scroll, which slid the ENTIRE app (persistent
                dashboard included) as one page on short pages like Users. */}
            <BasixFooter />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

SidebarWithHeader.defaultProps = { header: null };
